import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Mail, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ResetPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { resetPassword } = useAuth();

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (cooldown > 0) {
      return;
    }

    try {
      setLoading(true);
      await resetPassword(email);
      setEmailSent(true);
      setCooldown(60);
      toast.success('Email de réinitialisation envoyé !');
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Erreur lors de l\'envoi de l\'email de réinitialisation.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-slate-900/20" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sm:mx-auto sm:w-full sm:max-w-md relative z-10"
      >
        <div className="flex justify-center">
          <div className="bg-blue-600 p-3 rounded-full">
            <Mail className="h-8 w-8 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          Mot de passe oublié ?
        </h2>
        <p className="mt-2 text-center text-sm text-slate-300">
          Entrez votre email pour recevoir un lien de réinitialisation
        </p>
        <p className="mt-2 text-center text-sm text-slate-400">
          Ou{' '}
          <Link to="/login" className="font-medium text-blue-400 hover:text-blue-300">
            retourner à la connexion
          </Link>
        </p>
      </motion.div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 py-8 px-4 shadow-2xl rounded-2xl sm:px-10 hover:border-white/20 transition-all duration-300">
          {emailSent && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-green-900/30 border border-green-500/30 rounded-lg"
            >
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-green-400">Email envoyé avec succès !</h3>
                  <p className="mt-1 text-xs text-green-300">
                    Consultez votre boîte de réception et suivez les instructions pour réinitialiser votre mot de passe.
                  </p>
                  <div className="mt-3 flex items-start space-x-2">
                    <AlertCircle className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-300">
                      Pensez à vérifier vos spams si vous ne voyez pas l'email dans votre boîte de réception.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300">
                Adresse email
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={cooldown > 0}
                  className="appearance-none block w-full px-3 py-2 bg-slate-800/50 border border-white/10 rounded-lg shadow-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="votre.email@exemple.com"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || cooldown > 0}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Envoi en cours...
                  </>
                ) : cooldown > 0 ? (
                  <>
                    <Clock className="h-5 w-5 mr-2" />
                    Réessayer dans {cooldown}s
                  </>
                ) : (
                  <>
                    <Mail className="h-5 w-5 mr-2" />
                    Envoyer le lien de réinitialisation
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 p-4 bg-blue-900/20 border border-blue-500/20 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-400">
                Le lien de réinitialisation sera valable pendant 1 heure. Pour des raisons de sécurité, vous ne recevrez un email que si cette adresse est associée à un compte.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}